import { useMemo } from 'react';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  Column,
  EllipsisWithTooltip,
  ExternalLink,
  Icons,
  NoData,
  TableHeader,
  Tooltip,
  makeActionsColumn,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { CopyToClipboard } from 'components/clickable-actions';
import { tableCacheKeys } from 'constants/tableCacheKeys';
import { EdgeConfigurationActions } from 'features/cluster/autoscaler/edge-configurations/_components/EdgeConfigurationActions';
import { EdgeConfigurationsEmptyState } from 'features/cluster/autoscaler/edge-configurations/_components/EdgeConfigurationsEmptyState';
import { useDeleteEdgeConfigurationDialog } from 'features/cluster/autoscaler/edge-configurations/_hooks/useDeleteEdgeConfigurationDialog';
import { useEdgeConfigurationAbilities } from 'features/cluster/autoscaler/edge-configurations/_hooks/useEdgeConfigurationAbilities';
import { useEdgeConfigurationDrawer } from 'features/cluster/autoscaler/edge-configurations/_hooks/useEdgeConfigurationDrawer';
import { useIsClusterOmniEnabled } from 'features/cluster/autoscaler/edge-locations/hooks';
import { makeEdgeLocationsLink } from 'features/cluster/autoscaler/edge-locations/utils';
import { useAssignOmniCredentialsDialog } from 'hooks/dialogs/cluster/assign-credentials/useAssignOmniCredentialsDialog';
import { useSetEdgeConfigurationAsDefaultMutation } from 'hooks/mutations/omni';
import { useNavigate } from '@cast/infrastructure-organization';

import { useEdgeConfigurationsWithDetailsQuery } from './_hooks/useEdgeConfigurationsWithDetailsQuery';
import { edgeConfigListId, edgeConfigurationCriteria } from './constants';
import { LinkedTemplatesCell } from './LinkedTemplatesCell';
import { EdgeLocationTableRow } from './types';
import {
  flattenEdgeLocationGroups,
  markFirstVisibleRowsByEdgeLocation,
} from './utils';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import {
  Search,
  SearchProvider,
  NoResults,
  SearchableTable,
  SearchableTableRecordsCount,
} from '@cast/shared-search-ui';

const cellRowBorderSx = {
  borderTop: '1px solid',
  borderColor: 'var(--theme-border-structural-neutral-subtle)',
} as const;

type EdgeConfigurationsProps = {
  testId?: string;
};

export const EdgeConfigurations = ({
  testId = 'edge-configurations',
}: EdgeConfigurationsProps) => {
  const { cluster } = useCluster();
  const navigate = useNavigate();
  const { canCreate } = useEdgeConfigurationAbilities();
  const { openEdgeConfigurationDrawer } = useEdgeConfigurationDrawer();
  const { openDeleteEdgeConfigurationDialog } =
    useDeleteEdgeConfigurationDialog();
  const { openOmniCredentialsDialog } = useAssignOmniCredentialsDialog();
  const { mutate: setAsDefault, isPending: isSettingDefault } =
    useSetEdgeConfigurationAsDefaultMutation();
  const { groups, isLoading, error, refetch } =
    useEdgeConfigurationsWithDetailsQuery();
  const { isClusterOmniEnabled, isLoading: isLoadingClusterDetails } =
    useIsClusterOmniEnabled({
      clusterId: cluster.id,
    });

  const rows = useMemo(() => flattenEdgeLocationGroups(groups), [groups]);

  const isPageLoading = isLoading || isLoadingClusterDetails;
  const hasConfigurations = rows.length > 0;

  const edgeLocationsLink = makeEdgeLocationsLink({
    clusterId: cluster.id,
  });

  if (!isPageLoading && !error && !hasConfigurations) {
    return (
      <EdgeConfigurationsEmptyState
        isClusterOmniEnabled={isClusterOmniEnabled}
        onClusterExtendActionClick={() =>
          openOmniCredentialsDialog({
            cluster,
            onCreateEdgeLocationClick: () => openEdgeConfigurationDrawer(),
          })
        }
        onCreateEdgeLocationActionClick={() => navigate(edgeLocationsLink)}
      />
    );
  }

  return (
    <SearchProvider
      criteria={edgeConfigurationCriteria}
      data={rows}
      dataAggregator={markFirstVisibleRowsByEdgeLocation}
      isLoading={isLoading}
      urlKey={edgeConfigListId}
      error={error}
    >
      <Stack height="100%" gap={24} data-testid={testId}>
        <Search />
        <Box flexGrow={1}>
          <SearchableTable<EdgeLocationTableRow>
            cacheKey={tableCacheKeys.EDGE_CONFIGURATIONS}
            testId="edge-configurations-table"
            fillParentHeight
            rowKey="id"
            sx={{
              '& .DS-Table-BodyRowWrapper': {
                borderTopWidth: '0 !important',
              },
              '& .DS-Table-firstRow .DS-Table-BodyCellWrapper': {
                borderTopWidth: '0 !important',
                borderTopStyle: 'none !important',
              },
              '& .DS-Table-firstRow .DS-Table-BodyCell > .MuiBox-root': {
                borderTopWidth: 0,
              },
            }}
            onRowClick={(_event, { row }) => {
              if (!row.configuration.id || !row.edgeLocationId) {
                return;
              }
              openEdgeConfigurationDrawer({
                edgeConfiguration: row.configuration,
              });
            }}
            components={{
              noResults: <NoResults entity="configuration" />,
              noData: <NoData entity="configuration" />,
              failed: <FailedToLoad refresh={refetch} />,
            }}
            outerHeader={
              <TableHeader
                recordsCount={
                  <SearchableTableRecordsCount title="configuration" />
                }
                actions={
                  <Button
                    testId="add-edge-configuration-button"
                    variant="text"
                    disabled={!canCreate}
                    onClick={() => openEdgeConfigurationDrawer()}
                  >
                    Create configuration
                  </Button>
                }
              />
            }
          >
            <Column<EdgeLocationTableRow>
              id="id"
              header="id"
              width={50}
              cellWrapperSx={cellRowBorderSx}
              renderCell={({ configuration }) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {configuration.id ? (
                    <CopyToClipboard text={configuration.id} iconSize="20px" />
                  ) : (
                    '-'
                  )}
                </Box>
              )}
            />
            <Column<EdgeLocationTableRow>
              id="edgeLocation"
              header="Edge location"
              minWidth={250}
              resizable
              bodyCellWrapperSx={{
                padding: 0,
              }}
              renderCell={({
                configuration,
                edgeLocationName,
                isFirstInGroup,
              }) => (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: '40px',
                    padding: '0px 16px',
                    backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
                    borderTop: isFirstInGroup ? '1px solid' : 'none',
                    borderRight: '1px solid',
                    borderRightColor:
                      tokens.theme.border.structural.neutralSubtle,
                    borderTopColor:
                      tokens.theme.border.structural.neutralSubtle,
                  }}
                >
                  {isFirstInGroup ? (
                    <ExternalLink
                      link={edgeLocationsLink}
                      noIcon
                      sx={{ typography: 'P12R' }}
                      onClick={(e) => e.stopPropagation()}
                      testId={`edge-location-link-${configuration.id}`}
                    >
                      {edgeLocationName}
                    </ExternalLink>
                  ) : (
                    ''
                  )}
                </Box>
              )}
              sortable
              accessor="edgeLocationName"
            />
            <Column<EdgeLocationTableRow>
              id="name"
              header="Configuration name"
              minWidth={200}
              cellWrapperSx={cellRowBorderSx}
              renderCell={({ configuration }) => (
                <Button
                  testId="edit-configuration-button"
                  variant="text"
                  startIcon={
                    configuration.default ? (
                      <Tooltip
                        title={`By default ${CAST_AI} will apply configuration from this template to every provisioned node.`}
                      >
                        <Icons.Star data-testid="default-template-icon" />
                      </Tooltip>
                    ) : (
                      <Icons.SlidersHorizontal />
                    )
                  }
                >
                  <EllipsisWithTooltip
                    TypographyProps={{ variant: 'P12M', color: 'inherit' }}
                  >
                    {configuration.name}
                  </EllipsisWithTooltip>
                </Button>
              )}
              resizable
            />
            <Column<EdgeLocationTableRow>
              id="template"
              header="Node template"
              minWidth={250}
              cellWrapperSx={cellRowBorderSx}
              renderCell={({ configuration }) => (
                <LinkedTemplatesCell config={configuration} />
              )}
            />
            <Column<EdgeLocationTableRow>
              id="nodes"
              header="Nodes"
              minWidth={100}
              cellWrapperSx={cellRowBorderSx}
              renderCell={({ configuration }) => (
                <Typography variant="P12R">
                  {configuration.edgeCount || '-'}
                </Typography>
              )}
            />
            {makeActionsColumn<EdgeLocationTableRow>({
              width: 60,
              cellWrapperSx: cellRowBorderSx,
              action: ({ configuration, edgeLocationId }) => (
                <EdgeConfigurationActions
                  config={configuration}
                  isSettingDefault={isSettingDefault}
                  onEditClick={() =>
                    openEdgeConfigurationDrawer({
                      edgeConfiguration: configuration,
                    })
                  }
                  onDeleteClick={() =>
                    openDeleteEdgeConfigurationDialog(configuration)
                  }
                  onSetAsDefaultClick={() =>
                    setAsDefault({
                      clusterId: cluster.id,
                      edgeLocationId: edgeLocationId,
                      edgeConfigurationId: configuration.id || '',
                    })
                  }
                />
              ),
            })}
          </SearchableTable>
        </Box>
      </Stack>
    </SearchProvider>
  );
};
